import { apiClient, assertOk } from "@/api/generated/client.ts"
import type { WeatherData, WeatherLocation } from "@/types/weatherTypes.ts"

// Hardcoded locations for each facility - will be replaced by backend location service
const FACILITY_LOCATIONS: Record<number, WeatherLocation> = {
  2: {
    id: 2,
    name: "BCO",
    location: "Appleton,WI", // Use city name instead of zip to avoid location resolution issues
  },
  4: {
    id: 4,
    name: "Plumb Thicket",
    location: "Harper,Kansas",
  },
  5: {
    id: 5,
    name: "Lawrence",
    location: "Lawrence,Kansas",
  },
  7: {
    id: 7,
    name: "SPL For Hire",
    location: "Green Bay,WI",
  },
}

/**
 * Main weather service function - calls backend API
 */
export const getWeatherForLocation = async (locationId: number): Promise<WeatherData> => {
  return await getWeatherFromBackend(locationId)
}

/**
 * Get weather data from backend API
 */
async function getWeatherFromBackend(locationId: number): Promise<WeatherData> {
  const location = FACILITY_LOCATIONS[locationId]
  if (!location) {
    throw new Error(`Weather location not configured for location ID ${locationId}`)
  }

  try {
    const { data, error, response } = await apiClient.POST("/api/Weather", {
      body: {
        location: location.location,
        locationId,
      },
    })
    const weatherResponse = assertOk(response, error, data)

    // Extract weather data from the backend response structure
    return {
      ...weatherResponse.weather, // Backend returns { locationId, weather: { ... }, metadata }
      windDirection: weatherResponse.weather.windDirection ?? undefined,
      precipitation: weatherResponse.weather.precipitation ?? undefined,
      lastUpdated: weatherResponse.weather.lastUpdated ?? undefined,
      dataProvider: weatherResponse.weather.dataProvider ?? undefined,
      moonPhase: weatherResponse.weather.moonPhase ?? undefined,
      moonIllumination: weatherResponse.weather.moonIllumination ?? undefined,
      moonrise: weatherResponse.weather.moonrise ?? undefined,
      moonset: weatherResponse.weather.moonset ?? undefined,
      sunrise: weatherResponse.weather.sunrise ?? undefined,
      sunset: weatherResponse.weather.sunset ?? undefined,
      source: "backend" as const,
    }
  } catch (error) {
    console.error("Backend weather API failed:", error)
    throw error
  }
}

/**
 * Helper function to get location configuration (will be replaced by backend location service)
 */
export const getFacilityLocation = (locationId: number): WeatherLocation | undefined => {
  return FACILITY_LOCATIONS[locationId]
}
